import React, { useState, useEffect } from 'react';
import { __ } from './translations';
import { RefreshCw, CheckCircle, XCircle, Clock } from 'lucide-react';

const WelcomeEmailStatus = () => {
    const [status, setStatus] = useState({
        total: 0,
        processed: 0,
        success: 0,
        failed: 0,
        in_progress: false,
        start_time: null,
        completed_at: null
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchStatus = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${boardMemberData.restURL}board-member/v1/welcome-emails-status`, {
                headers: {
                    'X-WP-Nonce': boardMemberData.nonce,
                }
            });
            
            if (!response.ok) {
                // If endpoint doesn't exist yet or returns an error, just hide the component
                if (response.status === 404) {
                    return;
                }
                throw new Error('Failed to fetch status');
            }
            
            const data = await response.json();
            setStatus(data);
            setError(null);
        } catch (err) {
            console.error('Error fetching welcome emails status:', err);
            // Don't show errors, just hide the component
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatus();

        // If process is in progress, poll for updates every 10 seconds
        let interval;
        if (status.in_progress) {
            interval = setInterval(fetchStatus, 10000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [status.in_progress]);

    const getProgressPercentage = () => {
        if (status.total === 0) return 0;
        return Math.round((status.processed / status.total) * 100);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleString();
    };

    const calculateTimeElapsed = () => {
        if (!status.start_time) return '';
        
        const start = new Date(status.start_time);
        const end = status.completed_at ? new Date(status.completed_at) : new Date();
        
        const diffMs = end - start;
        const diffMins = Math.floor(diffMs / 60000);
        const diffSecs = Math.floor((diffMs % 60000) / 1000);
        
        return `${diffMins}m ${diffSecs}s`;
    };

    // Don't show anything if no active process and no history
    if ((loading && !status.total) || (!status.total && !status.in_progress)) {
        return null;
    }

    // If there's an error, also don't show anything to avoid disrupting the UI
    if (error) {
        return null;
    }

    return (
        <div className="mt-2 mb-6 bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                    {__('Welcome Emails Status')}
                </h3>
                <button 
                    onClick={fetchStatus}
                    className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
                    title={__('Refresh Status')}
                >
                    <RefreshCw size={16} />
                </button>
            </div>

            <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-3 rounded border border-blue-200 text-center">
                    <div className="text-2xl font-bold text-blue-600">{status.total}</div>
                    <div className="text-sm text-blue-800">{__('Total')}</div>
                </div>
                <div className="bg-green-50 p-3 rounded border border-green-200 text-center">
                    <div className="text-2xl font-bold text-green-600">{status.success}</div>
                    <div className="text-sm text-green-800">{__('Success')}</div>
                </div>
                <div className="bg-red-50 p-3 rounded border border-red-200 text-center">
                    <div className="text-2xl font-bold text-red-600">{status.failed}</div>
                    <div className="text-sm text-red-800">{__('Failed')}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded border border-gray-200 text-center">
                    <div className="text-2xl font-bold text-gray-600">{status.total - status.processed}</div>
                    <div className="text-sm text-gray-800">{__('Remaining')}</div>
                </div>
            </div>

            <div className="mb-4">
                <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-blue-700">
                        {__('Progress')}: {getProgressPercentage()}%
                    </span>
                    <span className="text-sm font-medium text-blue-700">
                        {status.processed} / {status.total}
                    </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                        className="bg-blue-600 h-2.5 rounded-full" 
                        style={{ width: `${getProgressPercentage()}%` }}
                    ></div>
                </div>
            </div>

            <div className="flex items-center justify-between text-sm border-t pt-4 text-gray-600">
                <div>
                    {status.in_progress ? (
                        <div className="flex items-center">
                            <Clock size={16} className="text-yellow-500 mr-1" />
                            <span>{__('In progress')}</span>
                        </div>
                    ) : status.completed_at ? (
                        <div className="flex items-center">
                            <CheckCircle size={16} className="text-green-500 mr-1" />
                            <span>{__('Completed')}</span>
                        </div>
                    ) : (
                        <div className="flex items-center">
                            <XCircle size={16} className="text-red-500 mr-1" />
                            <span>{__('Not started')}</span>
                        </div>
                    )}
                </div>
                
                <div className="flex gap-4">
                    {status.start_time && (
                        <div>
                            <span className="font-semibold">{__('Started')}:</span> {formatDate(status.start_time)}
                        </div>
                    )}
                    
                    {status.completed_at && (
                        <div>
                            <span className="font-semibold">{__('Completed')}:</span> {formatDate(status.completed_at)}
                        </div>
                    )}
                    
                    {status.start_time && (
                        <div>
                            <span className="font-semibold">{__('Time elapsed')}:</span> {calculateTimeElapsed()}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WelcomeEmailStatus;